import React, { useState, useEffect } from 'react';
import {
  Drawer,
  IconButton,
  Theme,
  Tooltip,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import CircularJSON from 'circular-json';
import styles from './ccf-app-navigation.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getContactsCount } from '../ccf-inbox/ccf-inbox.slice';
import { Navigation } from '../../enums/navigation-menus';
import { useHistory } from 'react-router-dom';
import {
  getQuickAppNavigationItems,
  getSelectedMenuName,
  globalActions,
} from '../global.app.slice';
import {
  NavigationItem,
  useNavigationItems,
} from '../ccf-navigation/useNavigationItems';
import { StorageKeys } from '@nice-ccf/core-sdk';
import { CcfNavItem } from './ccf-nav-item';
import { createStyles, makeStyles } from '@mui/styles';
import {
  CcfAppEnlighten,
  CcfAppSchedule,
  CcfCustomerCard,
  CcfDirectory,
  CcfQuickReplies,
  getPanelAppNavigationItems,
  CcfAppHamburgerMenu,
  CCfRtig,
} from '@nice-ccf/ui-components';
import {
  CcfCalendarIcon,
  CcfCustomerCardIcon,
  CcfDirectoryIcon,
  CcfSentimentIcon,
  CcfEnlightenIcon,
  CcfFlashIcon,
  useTranslator,
  CcfBox,
} from '@nice-ccf/ui-controls';
//import MenuIcon from '@mui/icons-material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { AppSpace } from '../../enums/app-space-tabs';
import { AppSpaceTab } from '../ccf-app-space/model/app-space-tab';

/* eslint-disable-next-line */
export interface CcfAppNavigationProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sidebarContainer: {
      justifyContent: 'center',
      flexDirection: 'column',
      height: '100%',
      padding: '6px',
      borderRight: `1px solid #dae2e8`,
      width: '56px',
      flexShrink: '0',
      background: 'var(--left-navbar-bg) 0% 0% no-repeat padding-box',
      opacity: '1',
    },
    sidebar: {
      position: 'static',
      flexShrink: 0,
    },
    moreMenuButton: {
      color: 'rgb(82, 107, 122)',
    },
    hamburger: {
      '& .MuiDrawer-paper': {
        width: '250px',
        backgroundColor: '#444d57',
      },
    },
  })
);

/**
 * Used for Application Navigation
 * @param props - ? - CcfAppNavigationProps
 * @example `<CcfAppNavigaton />`
 */
export function CcfAppNavigation(props: CcfAppNavigationProps) {
  const inboxCount = useSelector(getContactsCount);
  const quickAppNavigationItemsLS = useSelector(getQuickAppNavigationItems);
  const { setSelectedMenu } = globalActions;
  const selectedMenuName = useSelector(getSelectedMenuName);
  const history = useHistory();
  const dispatch = useDispatch();
  const [translate] = useTranslator();
  const theme: Theme = useTheme();
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'), { noSsr: true });
  const quickAppNavigationItems: NavigationItem[] = useNavigationItems(
    false,
    true
  );

  const [leftNavMenus, setLeftNavMenus] = useState(quickAppNavigationItems);
  const [isDrawerOpen, toggleDrawer] = useState(false);
  const { sidebarContainer, sidebar, moreMenuButton, hamburger } = useStyles();

  const appSpaceTabs: AppSpaceTab[] = [
    {
      icon: <CcfCustomerCardIcon />,
      element: <CcfCustomerCard />,
      tooltip: translate('contactCard'),
      isActive: false,
      menuName: AppSpace.CUSTOMERCARD,
    },
    {
      icon: <CcfCalendarIcon />,
      element: <CcfAppSchedule isfullView={false} />,
      tooltip: translate('schedule'),
      isActive: true,
      menuName: AppSpace.SCHEDULE,
    },
    {
      icon: <CcfDirectoryIcon />,
      element: <CcfDirectory isFullView={false} />,
      tooltip: translate('directory'),
      isActive: true,
      menuName: AppSpace.DIRECTORY,
    },
    {
      icon: <CcfSentimentIcon className={styles.tabicon} />,
      element: <CCfRtig resourceBaseUrl={'.'} />,
      tooltip: translate('sentiment'),
      isActive: false,
      menuName: AppSpace.SENTIMENT,
    },
    // {
    //   icon: <CcfConversationOutlineIcon className={styles.conversationIcon} />,
    //   element: <CcfConversations />,
    //   tooltip: 'conversations',
    //   isActive: true,
    //   menuName: AppSpace.CONVERSATIONS,
    // },
    {
      icon: <CcfEnlightenIcon className={styles.tabicon} />,
      element: <CcfAppEnlighten />,
      tooltip: translate('coPilot'),
      isActive: true,
      menuName: AppSpace.ENLIGHTEN,
    },
    {
      icon: <CcfFlashIcon />,
      element: <CcfQuickReplies />,
      tooltip: translate('quickReply'),
      isActive: true,
      menuName: AppSpace.QUICK_REPLY,
    },
  ];
  const panelAppNavigationItems: any[] = appSpaceTabs;

  useEffect(() => {
    const checkNavigationItemsLS = JSON.parse(
      localStorage.getItem(StorageKeys.CXONE_NAVIGATION_ITEMS) || '{}'
    );
    const isLocalStorageEmpty =
      Object.keys(checkNavigationItemsLS).length === 0;
    const lastLoginUserId = localStorage.getItem(
      StorageKeys.LAST_LOGGED_IN_AGENT_ID
    );
    const currentUser = JSON.parse(
      localStorage.getItem(StorageKeys.USER_INFO) || '{}'
    );
    const quickAppNavigationItemsLS = quickAppNavigationItems.map((item) => {
      return {
        menuName: item.menuName,
        contactApp: item.contactApp,
        tooltip: item.tooltip,
      };
    });
    const panelAppNavigationItemsLS = panelAppNavigationItems.map((item) => {
      return {
        menuName: item.menuName,
        tooltip: item.tooltip,
        isActive: item.isActive,
      };
    });
    if (
      isLocalStorageEmpty ||
      (lastLoginUserId && currentUser.icAgentId !== lastLoginUserId)
    ) {
      const CXoneNavigationItems = {
        quickApps: quickAppNavigationItemsLS,
        panelApps: panelAppNavigationItemsLS,
      };
      localStorage.setItem(
        StorageKeys.CXONE_NAVIGATION_ITEMS,
        CircularJSON.stringify(CXoneNavigationItems)
      );
      dispatch(
        globalActions.initializeCXoneNavigationItems(CXoneNavigationItems)
      );
      setLeftNavMenus(CXoneNavigationItems.quickApps);
    } else {
      // Quick apps are draggable/sortable, that's why we need order
      const quickAppsOrder: string[] = checkNavigationItemsLS.quickApps.map(
        (item: NavigationItem) => item.menuName
      );
      if (quickAppNavigationItems) {
        quickAppNavigationItemsLS.sort((itemOne, itemTwo) => {
          return (
            quickAppsOrder.indexOf(itemOne.menuName) -
            quickAppsOrder.indexOf(itemTwo.menuName)
          );
        });
      }
      const panelAppNavigationItemsLS = panelAppNavigationItems.map((item) => {
        return {
          menuName: item.menuName,
          isActive: item.isActive,
          tooltip: item.tooltip,
          icon: item.icon,
        };
      });
      const quickApps =
        quickAppNavigationItemsLS || checkNavigationItemsLS.quickApps;
      dispatch(
        globalActions.initializeCXoneNavigationItems({
          quickApps,
          panelApps: panelAppNavigationItemsLS,
        })
      );
      setLeftNavMenus(quickApps);
    }
  }, [isSmView, quickAppNavigationItems.length]);

  useEffect(() => {
    const orderedNavigationList: NavigationItem[] = [];
    if (!isSmView && selectedMenuName === Navigation.CUSTOMERCARD) {
      dispatch(setSelectedMenu({ name: Navigation.INTERACTION }));
    }
    quickAppNavigationItemsLS.forEach((item) => {
      const menuItem = quickAppNavigationItems.find(
        (menu) => menu.menuName === item.menuName
      );
      menuItem && orderedNavigationList.push(menuItem);
    });
    setLeftNavMenus(orderedNavigationList);
  }, [quickAppNavigationItemsLS, isSmView, quickAppNavigationItems.length]);

  return (
    <>
      <nav className={styles.sidebarContainer} id="agent_leftnav_container">
        <Stack
          className={styles.sidebar}
          direction={{
            xs: 'row',
            sm: 'row',
            md: 'row',
            lg: 'row',
            xl: 'column',
          }}
        >
          {leftNavMenus.length > 0 &&
            leftNavMenus.map(({ menuName, ...rest }: NavigationItem) => {
              return (
                <CcfNavItem key={menuName} menuName={menuName} {...rest} />
              );
            })}
        </Stack>

        <CcfBox>
          <Tooltip title={translate('more')}>
            <IconButton
              className={moreMenuButton}
              onClick={() => toggleDrawer(true)}
              aria-label="More"
              data-testid="more"
              id="agent_leftnav_moreIcon"
            >
              <MoreHorizIcon style={{ fill: 'rgba(0, 0, 0, 1.54)' }} />
            </IconButton>
          </Tooltip>
        </CcfBox>
      </nav>
      <Drawer
        anchor={'left'}
        open={isDrawerOpen}
        className={hamburger}
        onClose={() => toggleDrawer(false)}
      >
        <CcfAppHamburgerMenu
          helpUrl="www.google.com"
          isDrawerOpen={isDrawerOpen}
          isAppSpaceMenu={false}
          toggleDrawer={toggleDrawer}
        />
      </Drawer>
    </>
  );
}

export default CcfAppNavigation;
